import { useEffect } from 'react';
import { toBool, withStyles } from 'vopay';
import { useParams, useHistory } from 'react-router-dom';
import { Title, UtilButton } from 'UI';
import styles from './ContactPage.module.scss';
import ContactDetail from './ContactDetail';
import { useContactDetailData } from 'shared/contacts';
import PaymentMethodDropdownMenu from '@/components/Customers/CustomersPage/PaymentMethodsDropdown/PaymentMethodsDropdown';
import { useTranslation } from 'react-i18next';
import { useNavMenu } from '@/providers/NavMenu.provider';
import { useClientData } from '@/providers/ClientData.provider';
import { useBlockingLoader } from 'shared';

const cn = withStyles(styles);

type ContactPageProps = {
  // Shows the new flow's embedded Receivables/Payables tabs. Only the orphan
  // /accounting/contacts/:contactID route sets it while the new Accounting
  // Software API is validated (ID-13335); the menu-linked page stays as on
  // develop.
  accountingEnabled?: boolean;
};

export default function ContactPage({ accountingEnabled = false }: ContactPageProps) {
  const history = useHistory();
  const { contactID } = useParams<{ contactID: string }>();
  const { t } = useTranslation('translations', { keyPrefix: 'Contact' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { setSubNavActiveModule } = useNavMenu();
  const { response } = useClientData();

  const detail = useContactDetailData(contactID, toBool(response.ClientAccountsEnabled), {
    loginUserName: response.LoginUserName,
  });

  const { contact, methods, fetching, setDefaultMethod } = detail;
  const blockingLoader = useBlockingLoader();

  useEffect(() => {
    setSubNavActiveModule('contactAccounts');
  }, []);

  // The header dropdown's set-default blocked the page in the old portal hook;
  // the shared hook stays silent, so the raise lives here now.
  async function handleSetDefaultMethod(...args: Parameters<typeof setDefaultMethod>) {
    blockingLoader.show();

    try {
      return await setDefaultMethod(...args);
    } finally {
      blockingLoader.hide();
    }
  }

  return (
    <div className={cn('contactPage')}>
      <UtilButton
        iconLeft="chevron_left"
        size="large"
        onClick={() => {
          if (history.length > 1) {
            history.goBack();
          } else {
            history.replace('/contacts');
          }
        }}
      >
        {history.length > 1
          ? tGlobal('Buttons.back', {
              defaultValue: 'Back',
            })
          : t('contacts', {
              defaultValue: 'Contacts',
            })}
      </UtilButton>

      {contact && (
        <div className={cn('pageHeader')}>
          <Title>
            {t('pageTitle', {
              defaultValue: 'Contact Details',
            })}
          </Title>

          <div className={cn('pageHeaderLinks')}>
            <PaymentMethodDropdownMenu
              methods={methods}
              fetching={fetching}
              setDefaultMethod={handleSetDefaultMethod}
              accountName={contact.ContactName}
              accountID={contact.ContactID}
              email={contact.EmailAddress}
              disabled={!contact.IsActive}
            />
          </div>
        </div>
      )}

      <ContactDetail contactID={contactID} detail={detail} accountingEnabled={accountingEnabled} />
    </div>
  );
}
